import type { ReactNode } from "react";
import { WidgetRenderer } from "../WidgetRenderer";
import { useWidgetData, useWidgetDataSlice } from "../hooks";
import {
  type DashboardParameterValue,
  type DashboardWidget,
  type WidgetDataRow,
  type WidgetInteractionPointContext,
} from "../types";

export interface DashboardWidgetTileProps {
  widget: DashboardWidget;
  params?: Record<string, DashboardParameterValue>;
  /**
   * Set when this tile is rendered only for widgets already passed
   * {@link WidgetGrid}'s `isWidgetVisible` filter (same `resolvedParams`).
   * Avoids duplicating that check inside {@link useWidgetDataSlice}.
   */
  visibilityPrechecked?: boolean;
  onInteraction?: (
    widget: DashboardWidget,
    row: WidgetDataRow,
    point?: WidgetInteractionPointContext,
  ) => void;
  headerActions?: ReactNode;
}

export function DashboardWidgetTile({
  widget,
  params,
  visibilityPrechecked,
  onInteraction,
  headerActions,
}: DashboardWidgetTileProps) {
  // Widgets gated behind a `requires` parameter render as nothing until
  // every required parameter is set. We pass `null` as the SQL so
  // `useWidgetData`'s `enabled: !!sql` guard keeps the network quiet,
  // then short-circuit to `null` after the hooks have all run (so we
  // don't violate the rules of hooks). WidgetGrid skips the wrapper div
  // when the child returns null, so the gated section disappears
  // cleanly instead of leaving a "Select X to view this widget"
  // placeholder.
  const { visible, sql, widgetParams } = useWidgetDataSlice(widget, params, {
    visibilityPrechecked,
  });

  const query = useWidgetData(sql, widgetParams);

  if (!visible) return null;

  return (
    <WidgetRenderer
      widget={widget}
      params={params}
      headerActions={headerActions}
      onInteraction={
        onInteraction ? (row, point) => onInteraction(widget, row, point) : undefined
      }
      data={{
        columns: query.data?.columns ?? [],
        rows: query.data?.rows ?? [],
        isLoading: query.isLoading,
        error: query.error,
      }}
    />
  );
}
